/**
 * Where the shores are, for the water shader's pale band (STYLE §7, shoreBand.ts): the site's shore
 * segments, and a grid of cells each listing the segments within the band's reach of any of its
 * points. A fragment reads its own cell's list and takes the nearest segment, so its distance to
 * the shore is exact wherever the band shows; beyond the reach, and off the grid, it reads the reach.
 * shoreBand.glsl mirrors shoreDistanceAt.
 */
import type { Segment, Vec2 } from '../geo/types';
import { SHORE_BAND } from './shoreBand';

export const SHORE_GRID = {
  /* The real site's shores put at most 36 segments in a 32 m cell with a 40 m reach, and 2.3 on
     average in a cell that holds any. */
  cellM: 32,
  /** The most segments a fragment looks through: the shader's loop bound. */
  maxCellSegments: 64,
  /** A cell stores offset x countBase + count in one float, exact while it stays under 2^24. */
  countBase: 128,
  /** Width of the index and segment textures, in texels. */
  textureWidth: 1024,
} as const;

export interface ShoreGrid {
  /** Layout metres (x east, y north) of the grid's first cell's corner. */
  readonly origin: Vec2;
  readonly cellM: number;
  readonly reachM: number;
  /** Cells along x and along y. */
  readonly size: readonly [number, number];
  /** Per cell, row by row: its first entry in `index` x countBase + its number of entries. */
  readonly cells: Float32Array;
  /** Segment numbers, the cells' lists one after another. */
  readonly index: Float32Array;
  /** Per segment its two ends, ax, ay, bx, by. */
  readonly segments: Float32Array;
}

export const EMPTY_SHORE_GRID: ShoreGrid = {
  origin: [0, 0],
  cellM: SHORE_GRID.cellM,
  reachM: SHORE_BAND.reachM,
  size: [1, 1],
  cells: new Float32Array(1),
  index: new Float32Array(0),
  segments: new Float32Array(0),
};

interface Box {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

function pointSegmentDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
  const length2 = dx * dx + dy * dy;
  const along = length2 > 0 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / length2 : 0;
  const t = Math.min(1, Math.max(0, along));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

function pointBoxDistance(p: Vec2, box: Box): number {
  const dx = Math.max(box.x0 - p[0], 0, p[0] - box.x1);
  const dy = Math.max(box.y0 - p[1], 0, p[1] - box.y1);
  return Math.hypot(dx, dy);
}

/* Liang-Barsky: whether any part of the segment lies in the box. */
function crossesBox(a: Vec2, b: Vec2, box: Box): boolean {
  const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
  const edges: readonly (readonly [number, number])[] = [
    [-dx, a[0] - box.x0],
    [dx, box.x1 - a[0]],
    [-dy, a[1] - box.y0],
    [dy, box.y1 - a[1]],
  ];
  let [t0, t1] = [0, 1];
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return false;
    } else if (p < 0) {
      t0 = Math.max(t0, q / p);
    } else {
      t1 = Math.min(t1, q / p);
    }
  }
  return t0 <= t1;
}

/* Apart, a segment and a box come closest at an end of the segment or a corner of the box. */
function segmentBoxDistance({ a, b }: Segment, box: Box): number {
  if (crossesBox(a, b, box)) return 0;
  const corners: readonly Vec2[] = [
    [box.x0, box.y0],
    [box.x1, box.y0],
    [box.x0, box.y1],
    [box.x1, box.y1],
  ];
  return Math.min(
    pointBoxDistance(a, box),
    pointBoxDistance(b, box),
    ...corners.map((c) => pointSegmentDistance(c, a, b))
  );
}

function gridFrame(shores: readonly Segment[], reachM: number): Pick<ShoreGrid, 'origin' | 'size'> {
  const ends = shores.flatMap(({ a, b }) => [a, b]);
  const [x0, y0, x1, y1] = ends.reduce(
    ([minX, minY, maxX, maxY], [x, y]) => [
      Math.min(minX, x),
      Math.min(minY, y),
      Math.max(maxX, x),
      Math.max(maxY, y),
    ],
    [Infinity, Infinity, -Infinity, -Infinity]
  );
  const origin: Vec2 = [x0 - reachM, y0 - reachM];
  const nx = Math.max(1, Math.ceil((x1 + reachM - origin[0]) / SHORE_GRID.cellM));
  const ny = Math.max(1, Math.ceil((y1 + reachM - origin[1]) / SHORE_GRID.cellM));
  return { origin, size: [nx, ny] };
}

/* Each cell's list: every segment within the reach of some point of the cell. */
function cellLists(
  shores: readonly Segment[],
  frame: Pick<ShoreGrid, 'origin' | 'size'>,
  reachM: number
): number[][] {
  const { origin, size } = frame;
  const cell = SHORE_GRID.cellM;
  const lists: number[][] = Array.from({ length: size[0] * size[1] }, () => []);
  const toCell = (v: number, o: number, n: number) =>
    Math.min(n - 1, Math.max(0, Math.floor((v - o) / cell)));
  shores.forEach((segment, n) => {
    const { a, b } = segment;
    const [i0, i1] = [
      toCell(Math.min(a[0], b[0]) - reachM, origin[0], size[0]),
      toCell(Math.max(a[0], b[0]) + reachM, origin[0], size[0]),
    ];
    const [j0, j1] = [
      toCell(Math.min(a[1], b[1]) - reachM, origin[1], size[1]),
      toCell(Math.max(a[1], b[1]) + reachM, origin[1], size[1]),
    ];
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const box = {
          x0: origin[0] + i * cell,
          y0: origin[1] + j * cell,
          x1: origin[0] + (i + 1) * cell,
          y1: origin[1] + (j + 1) * cell,
        };
        if (segmentBoxDistance(segment, box) <= reachM) lists[j * size[0] + i]!.push(n);
      }
    }
  });
  return lists;
}

/**
 * The grid of the shores' segments within `reachM` of each cell. A cell listing more segments than
 * the shader looks through would give fragments there a wrong distance, so it is refused.
 */
export function buildShoreGrid(
  shores: readonly Segment[],
  reachM: number = SHORE_BAND.reachM
): ShoreGrid {
  if (shores.length === 0) return { ...EMPTY_SHORE_GRID, reachM };
  const frame = gridFrame(shores, reachM);
  const lists = cellLists(shores, frame, reachM);
  const fullest = lists.reduce((most, list) => Math.max(most, list.length), 0);
  if (fullest > SHORE_GRID.maxCellSegments) {
    throw new RangeError(
      `A shore cell lists ${fullest} segments; the shader looks through ${SHORE_GRID.maxCellSegments}`
    );
  }
  const cells = new Float32Array(lists.length);
  let offset = 0;
  lists.forEach((list, n) => {
    cells[n] = list.length > 0 ? offset * SHORE_GRID.countBase + list.length : 0;
    offset += list.length;
  });
  return {
    ...frame,
    cellM: SHORE_GRID.cellM,
    reachM,
    cells,
    index: Float32Array.from(lists.flat()),
    segments: Float32Array.from(shores.flatMap(({ a, b }) => [a[0], a[1], b[0], b[1]])),
  };
}

/** The first entry and the number of entries of a cell's list, as the shader decodes them. */
export function cellSegments(grid: ShoreGrid, cell: number): { offset: number; count: number } {
  const packed = grid.cells[cell] ?? 0;
  return {
    offset: Math.floor(packed / SHORE_GRID.countBase),
    count: packed % SHORE_GRID.countBase,
  };
}

/** The distance from a layout point to the nearest shore, the reach where none is within it. */
export function shoreDistanceAt(grid: ShoreGrid, p: Vec2): number {
  const i = Math.floor((p[0] - grid.origin[0]) / grid.cellM);
  const j = Math.floor((p[1] - grid.origin[1]) / grid.cellM);
  if (i < 0 || j < 0 || i >= grid.size[0] || j >= grid.size[1]) return grid.reachM;
  const { offset, count } = cellSegments(grid, j * grid.size[0] + i);
  let best = grid.reachM;
  for (let k = 0; k < count; k++) {
    const s = (grid.index[offset + k] ?? 0) * 4;
    const [ax, ay, bx, by] = [
      grid.segments[s]!,
      grid.segments[s + 1]!,
      grid.segments[s + 2]!,
      grid.segments[s + 3]!,
    ];
    best = Math.min(best, pointSegmentDistance(p, [ax, ay], [bx, by]));
  }
  return best;
}
